import React, { useEffect, useRef } from "react";
import "./HtmlText.css";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { gateThirdPartyHtml } from "../../utils/gateThirdPartyHtml";
import { useAcceptAll } from "../../hooks/useCookieChoiceMade";
import { useViewerCopy } from "../../utils/viewerLanguage";
import { loadGoogleFontsInHtml } from "../../utils/googleFontLoader";

export function scaleFontSizesInHtml(html, zoomScale) {
  if (!html) return '';

  return html.replace(/font-size\s*:\s*(\d+(?:\.\d+)?)(px|pt)/gi, (_, size, unit) => {
    const scaledSize = parseFloat(size) * zoomScale;
    return `font-size: ${scaledSize}${unit}`;
  });
}

const HtmlText = ({ htmlContent, zoom, element, onClick, elemClick, handleClick, handleAction, resizeDimensions, dimensions, fitToBox }) => {
  const containerRef = useRef(null);
  const acceptAll = useAcceptAll();
  useViewerCopy();

  const zoomScale = parseFloat(zoom?.value ?? 1);
  const sanitizedHtml = sanitizeHtml(scaleFontSizesInHtml(htmlContent, zoomScale));
  const scaledHtml = acceptAll ? sanitizedHtml : gateThirdPartyHtml(sanitizedHtml);

  useEffect(() => {
    loadGoogleFontsInHtml(htmlContent);
  }, [htmlContent]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || acceptAll) return undefined;
    const openSettings = (event) => {
      if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") return;
      if (!event.target.closest("[data-kz-cookie-settings]")) return;
      event.preventDefault();
      event.stopPropagation();
      window.dispatchEvent(new CustomEvent("openCookieConsentModal"));
    };
    container.addEventListener("click", openSettings);
    container.addEventListener("keydown", openSettings);
    return () => {
      container.removeEventListener("click", openSettings);
      container.removeEventListener("keydown", openSettings);
    };
  }, [acceptAll]);

  useEffect(() => {
    const container = containerRef.current;
    // Only set up click handlers for data-page-link spans
    // We need elemClick, handleClick, handleAction to follow the exact same pattern as onClick handler
    if (!container || !element || (!elemClick && !handleClick && !onClick)) return;

    // Handle clicks on elements with data-page-link attribute
    const handlePageLinkClick = (event) => {
      const target = event.target;
      const pageLinkSpan = target.closest('[data-page-link]');
      
      if (pageLinkSpan) {
        event.preventDefault();
        event.stopPropagation();
        
        const pageNumber = pageLinkSpan.getAttribute('data-page-link');
        
        // Create a synthetic element with page_link value, following the exact same pattern
        // as the onClick handler in Element.js
        const syntheticElement = {
          ...element,
          value: {
            ...element?.value,
            page_link: parseInt(pageNumber)
          }
        };
        
        // Follow the exact same execution order as the original onClick handler:
        // 1. elemClick for stats tracking - pass syntheticElement so it has page_link in value
        // This ensures stats can read page_link from element.value (same as Hotspots)
        if (elemClick && element?.id) {
          elemClick(element.id, event, syntheticElement);
        }
        
        // 2. handleClick for navigation (checks syntheticElement.value.page_link)
        if (handleClick) {
          handleClick(syntheticElement);
        }
        
        // 3. handleAction if dropdown_data exists
        if (handleAction && element?.dropdown_data) {
          handleAction(element);
        }
        
        // If we don't have the individual handlers but have onClick, call it with the event
        // This maintains backward compatibility
        if (!elemClick && !handleClick && onClick) {
          onClick(event);
        }
        
        return false;
      }
    };

    container.addEventListener('click', handlePageLinkClick);

    return () => {
      container.removeEventListener('click', handlePageLinkClick);
    };
  }, [htmlContent, element, onClick, elemClick, handleClick, handleAction]);

  return (
    <div
      ref={containerRef}
      className={fitToBox ? "text-light html-text-content html-text-content--boxed" : "p-1 text-light html-text-content"}
      style={fitToBox ? {
        width: `${resizeDimensions?.[element?.id]?.width || dimensions?.width}px`,
        height: `${resizeDimensions?.[element?.id]?.height || dimensions?.height}px`,
      } : undefined}
      dangerouslySetInnerHTML={{ __html: scaledHtml }}
    />
  );
};


export default React.memo(HtmlText);